import { useState } from "react";
import {
  Form,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { nativeSelectClass } from "@/tenax/tasks/taskData";
import { HolderFields } from "./HolderFields";
import { useDay, useHolderValues } from "./racketHooks";
import {
  useGoodsOutReasons,
  useVariants,
  variantLabel,
  type Variant,
} from "./racketData";
import { HolderName } from "./RelatedLoans";

interface GoodsOut {
  id: number;
  reason_id: number;
  contact_id: number | null;
  company_id: number | null;
  variant_id: number | null;
  description: string | null;
  qty: number;
  shipped_at: string;
  unit_cost: number | null;
}

// Goods out without a sale (D-026, D-040): kit, gift, seeding, prize,
// athletes' gear. The cost at shipping is kept for the margin of the
// initiatives (D-035). The racket turned into a test racket is recorded by
// "New racket", not here.
export const GoodsOutsSection = () => {
  const translate = useTranslate();
  const day = useDay();
  const [adding, setAdding] = useState(false);
  const { data: reasons = [] } = useGoodsOutReasons();
  const { data: variants = [] } = useVariants();
  const { data: outs = [] } = useGetList<GoodsOut>("goods_outs", {
    pagination: { page: 1, perPage: 30 },
    sort: { field: "shipped_at", order: "DESC" },
  });
  return (
    <section
      aria-label={translate("tenax.rackets.goods_outs.title")}
      className="flex flex-col gap-2"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {translate("tenax.rackets.goods_outs.title")}
        </h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setAdding(!adding)}
        >
          {translate("tenax.rackets.goods_outs.new")}
        </Button>
      </div>
      {adding ? (
        <Form defaultValues={{ contact_id: null, company_id: null }}>
          <GoodsOutForm variants={variants} onDone={() => setAdding(false)} />
        </Form>
      ) : null}
      {outs.length ? (
        <ul className="flex flex-col gap-1">
          {outs.map((out) => (
            <li
              key={out.id}
              className="flex flex-wrap gap-x-3 rounded-md border px-3 py-1.5 text-sm"
            >
              <span>{day(out.shipped_at)}</span>
              <span className="font-medium">
                {reasons.find((reason) => reason.id === out.reason_id)?.name}
              </span>
              <span className="min-w-0 flex-1">
                {out.qty} ×{" "}
                {out.description ??
                  variantLabel(variants.find((v) => v.id === out.variant_id))}
              </span>
              <HolderName
                contactId={out.contact_id}
                companyId={out.company_id}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("tenax.rackets.goods_outs.empty")}
        </p>
      )}
    </section>
  );
};

const GoodsOutForm = ({
  variants,
  onDone,
}: {
  variants: Variant[];
  onDone: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { contactId, companyId } = useHolderValues();
  const { data: reasons = [] } = useGoodsOutReasons();
  const choices = reasons.filter(
    (reason) => reason.active && reason.key !== "test_racket_conversion",
  );
  const [reasonId, setReasonId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [description, setDescription] = useState("");
  const [qty, setQty] = useState("1");
  const [cost, setCost] = useState("");
  const reason = reasonId || String(choices[0]?.id ?? "");

  const save = async () => {
    try {
      await dataProvider.create("goods_outs", {
        data: {
          reason_id: Number(reason),
          contact_id: contactId,
          company_id: companyId,
          variant_id: variantId ? Number(variantId) : null,
          description: description.trim() || null,
          qty: Number(qty),
          unit_cost: cost === "" ? null : Number(cost),
        },
      });
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["goods_outs"] });
    notify("tenax.rackets.goods_outs.saved", { type: "info" });
    onDone();
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="goods-out-reason">
            {translate("tenax.rackets.goods_outs.reason")}
          </Label>
          <select
            id="goods-out-reason"
            className={nativeSelectClass}
            value={reason}
            onChange={(event) => setReasonId(event.target.value)}
          >
            {choices.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="goods-out-variant">
            {translate("tenax.rackets.fields.variant_id")}
          </Label>
          <select
            id="goods-out-variant"
            className={nativeSelectClass}
            value={variantId}
            onChange={(event) => setVariantId(event.target.value)}
          >
            <option value="">—</option>
            {variants.map((variant) => (
              <option key={variant.id} value={variant.id}>
                {variantLabel(variant)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="goods-out-description">
            {translate("tenax.rackets.goods_outs.description")}
          </Label>
          <Input
            id="goods-out-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="goods-out-qty">
              {translate("tenax.rackets.goods_outs.qty")}
            </Label>
            <Input
              id="goods-out-qty"
              type="number"
              min="1"
              value={qty}
              onChange={(event) => setQty(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="goods-out-cost">
              {translate("tenax.rackets.goods_outs.unit_cost")}
            </Label>
            <Input
              id="goods-out-cost"
              type="number"
              min="0"
              step="0.01"
              value={cost}
              onChange={(event) => setCost(event.target.value)}
            />
          </div>
        </div>
      </div>
      <HolderFields />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {translate("ra.action.cancel")}
        </Button>
        <Button
          type="button"
          disabled={
            !reason || (!variantId && !description.trim()) || !(Number(qty) > 0)
          }
          onClick={save}
        >
          {translate("ra.action.save")}
        </Button>
      </div>
    </div>
  );
};
